import { inject, Injectable } from '@angular/core';

import { sha256 } from 'js-sha256';
import moment from 'moment';
import { Subject } from 'rxjs';

import { ACCESS_TOKEN_KEY, REFRESH_DATETIME_EXPIRE, REFRESH_TOKEN_KEY } from '@core/helper';
import { IResponseMeta } from '@core/interfaces/response.interface';
import { IToken } from '@core/interfaces/token.interface';
import { SessionStorageService } from '@core/services/session-storage.service';
import { environment as env } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  refreshedToken$: Subject<boolean> = new Subject();

  private _accessToken: string;
  private _refreshToken: string;
  private _refreshDateTimeExpire: string;
  private _initDate: Date;
  private readonly storage = inject(SessionStorageService);

  get accessToken(): string {
    if (!this._accessToken) {
      this._accessToken = this.storage.get(ACCESS_TOKEN_KEY);
    }
    return this._accessToken;
  }

  get refreshToken(): string {
    if (!this._refreshToken) {
      this._refreshToken = this.storage.get(REFRESH_TOKEN_KEY);
    }
    return this._refreshToken;
  }

  private get refreshDateTimeExpire(): string {
    if (!this._refreshDateTimeExpire) {
      this._refreshDateTimeExpire = this.storage.get(REFRESH_DATETIME_EXPIRE);
    }
    return this._refreshDateTimeExpire;
  }

  private get initDate(): Date {
    if (!this._initDate) {
      this._initDate = new Date(JSON.parse(this.storage.get('CURRENT_DATE')));
    }
    return this._initDate;
  }

  /**
   * set tokens
   */
  setTokens(meta: IResponseMeta): void {
    this._accessToken = meta.accessToken;
    this._refreshToken = meta.refreshToken;
    this._refreshDateTimeExpire = meta.refreshTokenDuration;
    this._initDate = new Date();
    this.saveInLocalStorage(meta);
    this.refreshedToken$.next(true);
  }

  /**
   * get tokens
   */
  getTokens(): IToken {
    return {
      accessToken: this.storage.get(ACCESS_TOKEN_KEY),
      refreshToken: this.storage.get(REFRESH_TOKEN_KEY),
    };
  }

  /**
   * clear tokens
   */
  clearTokens(): void {
    this.storage.remove(ACCESS_TOKEN_KEY);
    this.storage.remove(REFRESH_TOKEN_KEY);
    this.storage.remove(REFRESH_DATETIME_EXPIRE);
    this._accessToken = null;
    this._refreshToken = null;
    this._refreshDateTimeExpire = null;
  }

  /**
   * get refresh expire time
   */
  getExpireSecondTime(): number {
    const dates = (this.refreshDateTimeExpire || '').split(':');
    const min = parseInt(dates[1], 10);
    const sec = parseInt(dates[2], 10) + min * 60;
    const d = moment();
    return sec - d.diff(this.initDate, 'second');
  }

  /**
   * check expire refresh time
   */
  isExpireRefreshTime(): boolean {
    return this.getExpireSecondTime() < 0;
  }

  getHelloToken(): string {
    const time = new Date().getTime().toString().slice(0, 10);
    const hash = sha256(`61184242-3c92-4b1d-97dc-be5b7b131c1d:${time}:${env.key}`).toLowerCase();
    const tp1 = btoa(hash);
    const tp2 = btoa(time.toString());
    return tp1 + '.' + tp2;
  }

  private saveInLocalStorage(meta: IResponseMeta): void {
    this.storage.set(ACCESS_TOKEN_KEY, meta.accessToken);
    this.storage.set(REFRESH_TOKEN_KEY, meta.refreshToken);
    this.storage.set(REFRESH_DATETIME_EXPIRE, meta.refreshTokenDuration);
    this.storage.set('CURRENT_DATE', this._initDate);
  }
}
